/**
 * ============================================================================
 * useRecordPMMutation — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/equipment/{id}/pm — record a new PM. When outcome
 *          is COMPLETED the server enforces a doc-controller co-signature;
 *          we mirror the requirement client-side for fast feedback.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type {
  PMOutcome,
  PMRecord,
  PMType,
} from '../EquipmentDetail/useEquipmentDetailQuery';

export interface RecordPMInput {
  equipmentId: string;
  performed_at_utc: string;
  pm_type: PMType;
  outcome: PMOutcome;
  work_description: string;
  parts_replaced: Array<Record<string, unknown>>;
  engineer_signature_bundle_id: string;
  /** Required when outcome = COMPLETED. */
  doc_controller_signature_bundle_id?: string;
}

export function useRecordPMMutation(): UseMutationResult<
  PMRecord,
  Error,
  RecordPMInput
> {
  return useMutation<PMRecord, Error, RecordPMInput>({
    mutationFn: ({ equipmentId, ...body }) =>
      apiFetch<PMRecord>(
        `/api/v1/equipment/${encodeURIComponent(equipmentId)}/pm`,
        {
          method: 'POST',
          body,
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
